import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
} from 'firebase/auth';

import Box from '@mui/material/Box';

import AppHeader from '../../components/app-header';
import AppFooter from '../../components/app-footer';
import Alert from '../../components/shared/Alert';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import Checkbox from '../../components/shared/Checkbox';
import ForgotPasswordModal from '../../components/modals/ForgotPassword';

import { withFirebase } from '../../firebase';

import { setCurrentUser, resetPassword } from '../../actions/userActions';

import './login-page.scss';

const LoginPage = ({ firebase, setUser, history }) => {
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [checked, setChecked] = useState(true);

  const [loading, setLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const [error, setError] = useState(false);
  const [mfaError, setMfaError] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);

  const [mfaVerificationCode, setMfaVerificationCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [totpRequired, setTotpRequired] = useState(false);

  const [multiFactorResolver, setMultiFactorResolver] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  useEffect(() => {
    try {
      const verifier = firebase.getRecaptchaVerifier('recaptcha-container');

      verifier.render().then(() => {
        setRecaptchaVerifier(verifier);
      });
    } catch (err) {
      console.error('reCAPTCHA init error:', err);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError(false);

    try {
      const authResult = await firebase.doSignInWithEmailAndPassword(
        email,
        password,
        checked
      );

      setUser(authResult);
      history.push('/dashboard');
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(firebase.auth, err);
          const hints = resolver.hints;

          const hasTOTP = hints.some((h) => h.factorId === 'totp');
          const hasSMS = hints.some((h) => h.factorId === 'phone');

          setMultiFactorResolver(resolver);

          if (hasTOTP) {
            setTotpRequired(true);
          } else if (hasSMS) {
            const hint = hints.find((h) => h.factorId === 'phone');
            const provider = new PhoneAuthProvider(firebase.auth);

            const id = await provider.verifyPhoneNumber(
              {
                multiFactorHint: hint,
                session: resolver.session,
              },
              recaptchaVerifier
            );

            setVerificationId(id);
            setMfaRequired(true);
          }
        } catch (mfaErr) {
          console.error(mfaErr);
          setError(true);
        }
      } else {
        console.error(err);
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMFA = async () => {
    setVerificationLoading(true);

    try {
      const cred = PhoneAuthProvider.credential(
        verificationId,
        mfaVerificationCode
      );

      const assertion =
        PhoneMultiFactorGenerator.assertion(cred);

      await multiFactorResolver.resolveSignIn(assertion);

      const user = await firebase.doGetCurrentUser();
      setUser(user);
      history.push('/dashboard');
    } catch (err) {
      console.error(err);
      setMfaError(true);
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleTOTP = async () => {
    setVerificationLoading(true);

    try {
      const index = multiFactorResolver.hints.findIndex(
        (h) => h.factorId === 'totp'
      );

      const assertion =
        TotpMultiFactorGenerator.assertionForSignIn(
          multiFactorResolver.hints[index].uid,
          totpCode
        );

      await multiFactorResolver.resolveSignIn(assertion);

      const user = await firebase.doGetCurrentUser();
      setUser(user);
      history.push('/dashboard');
    } catch (err) {
      console.error(err);
      setMfaError(true);
    } finally {
      setVerificationLoading(false);
    }
  };

  const onResetPassword = async (email) => {
    try {
      setIsOpen(false);

      const res = await dispatch(resetPassword(email));
      console.log(res);
      if (res) {

      }
    } catch (err) {
      setResetPasswordError(err?.message || 'Server Error');
    }
  };

  return (
    <div className="login-page">
      <AppHeader />

      <div className="login-form-container">
        <h3>Sign In</h3>

        {!mfaRequired && !totpRequired && (
          <form onSubmit={handleSubmit}>
            <Box sx={{ mt: 1 }}>
              <Input
                label="Email Address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(false);
                }}
                placeholder="Email"
                type="email"
              />
            </Box>

            <Box sx={{ mt: 1 }}>
              <Input
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                placeholder="Password"
              />
            </Box>

            <div className="actions">
              <Checkbox
                checked={checked}
                onChange={() => setChecked(!checked)}
                label="Keep me signed in"
              />

              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                text="Sign in"
                loading={loading}
              />
            </div>

            <div className="primary pointer m-t-md" onClick={() => setIsOpen(true)}>
              Forgot password?
            </div>
          </form>
        )}

        {mfaRequired && (
          <>
            <Input
              label="Code"
              value={mfaVerificationCode}
              onChange={(e) => setMfaVerificationCode(e.target.value)}
            />

            <Button
              sx={{ mt: 2 }}
              onClick={handleMFA}
              loading={verificationLoading}
              disabled={!mfaVerificationCode}
              text="Verify Code"
            />
          </>
        )}

        {totpRequired && (
          <>
            <Input
              label="Authenticator Code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
            />

            <Button
              sx={{ mt: 2 }}
              onClick={handleTOTP}
              loading={verificationLoading}
              disabled={!totpCode}
              text="Verify Code"
            />
          </>
        )}
      </div>

      <Alert
        variant="error"
        message="Invalid email or password"
        open={error}
        onClose={() => setError(false)}
      />

      <Alert
        variant="error"
        message="Error verifying code"
        open={mfaError}
        onClose={() => setMfaError(false)}
      />

      <Alert
        variant="error"
        message={resetPasswordError}
        open={!!resetPasswordError}
        onClose={() => setResetPasswordError(null)}
      />

      <Alert
        variant="success"
        message="Please follow instructions that we sent you"
        open={resetPasswordSuccess}
        onClose={() => setResetPasswordSuccess(false)}
      />

      <ForgotPasswordModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={onResetPassword}
      />

      <AppFooter link="sign-up" />
    </div>
  );
};

LoginPage.propTypes = {
  firebase: PropTypes.object.isRequired,
  setUser: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(
  withFirebase(connect(null, { setUser: setCurrentUser })(LoginPage))
);
